import React from 'react';
import { AlertTriangle, CheckCircle2, FileText, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DOC_STATE_LABEL, DOC_STATE_TONE } from '@/components/writer/writerDocState';
import type { WriterVersionSummary } from '@/components/writer/WriterVersionScrubber';
import type { WriterDocState } from '@/hooks/useWriterDocState';

interface Props {
  docState: WriterDocState | null;
  versions: WriterVersionSummary[];
  onOpenPdf: () => void;
  /** Closes the PDF sheet — the "Chat" side of the segmented control. */
  onShowChat?: () => void;
  /** Which surface is currently in front. */
  active?: 'chat' | 'pdf';
}

/**
 * Sticky one-line status strip for the compact (phone / tablet-portrait)
 * Writer layout. Answers the single most common away-from-desk question —
 * "did my draft compile?" — without leaving the chat, and carries the
 * always-visible Chat | PDF segmented control.
 */
const WriterMobileStatusBar: React.FC<Props> = ({
  docState,
  versions,
  onOpenPdf,
  onShowChat,
  active = 'chat',
}) => {
  const inFlight = versions.some(
    (v) => v.compile_status === 'pending' || v.compile_status === 'running',
  );
  const latest = versions[0] ?? null;

  let Icon = FileText;
  let tone = 'text-muted-foreground';
  let label = docState ? DOC_STATE_LABEL[docState] : 'No draft yet';
  let dot = 'bg-muted-foreground/40';

  if (inFlight) {
    Icon = Loader2;
    tone = 'text-sky-600 dark:text-sky-400';
    label = 'Compiling…';
    dot = 'bg-sky-500';
  } else if (latest?.compile_status === 'failed') {
    Icon = AlertTriangle;
    tone = 'text-destructive';
    label = 'Compile failed';
    dot = 'bg-destructive';
  } else if (latest?.compile_status === 'success') {
    Icon = CheckCircle2;
    tone = 'text-emerald-600 dark:text-emerald-400';
    label = docState ? DOC_STATE_LABEL[docState] : 'Up to date';
    dot = 'bg-emerald-500';
  } else if (docState) {
    tone = DOC_STATE_TONE[docState];
  }

  const segment = (isActive: boolean) =>
    cn(
      'rounded-full px-3 py-1 text-xs font-medium transition',
      isActive
        ? 'bg-background text-foreground shadow-sm'
        : 'text-muted-foreground hover:text-foreground',
    );

  return (
    <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border/60 bg-background/95 px-4 py-2 backdrop-blur">
      <div className={cn('flex min-w-0 items-center gap-2 text-xs font-medium', tone)}>
        <Icon className={cn('h-3.5 w-3.5 shrink-0', inFlight && 'animate-spin')} />
        <span className="truncate" aria-live="polite">
          {label}
        </span>
        {versions.length > 0 && (
          <span className="shrink-0 text-muted-foreground">· v{versions.length}</span>
        )}
      </div>
      <div
        role="tablist"
        aria-label="Writer surface"
        className="flex shrink-0 items-center gap-1 rounded-full border border-border/70 bg-muted/50 p-0.5"
      >
        <button
          type="button"
          role="tab"
          aria-selected={active === 'chat'}
          onClick={onShowChat}
          className={segment(active === 'chat')}
        >
          Chat
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={active === 'pdf'}
          onClick={onOpenPdf}
          className={cn(segment(active === 'pdf'), 'inline-flex items-center gap-1.5')}
        >
          <span className={cn('h-1.5 w-1.5 rounded-full', dot)} aria-hidden />
          PDF
        </button>
      </div>
    </div>
  );
};

export default WriterMobileStatusBar;
